// TODO: Consider loading the rules from the backend (/misc/validate.php) so we can reuse the same patterns and error messages for both client and server side validation.


/**
 * // raw validation rule set
 * @typedef {Object} ValidationRuleInternational
 * @property {string} pattern - The validation pattern to test against
 * @property {string} [en] - The message to show if validation fails in English
 * @property {string} [sv] - The message to show if validation fails in Swedish
 * @property {string} [de] - The message to show if validation fails in German
 * @property {string} [da] - The message to show if validation fails in Danish
 * @property {string} [fi] - The message to show if validation fails in Finnish
 * @property {string} [fr] - The message to show if validation fails in French
 * @property {string} [nl] - The message to show if validation fails in Dutch
 */

/**
 * @typedef {Object} ValidationRule
 * @property {string} pattern
 * @property {string} invalidMessage
 */


/**
 * @typedef {string} ParsingError
 */

export const formValidation = {
    /**
     * Handles form validation and submission using regex patterns and custom error messages.
     * 
     * @param {HTMLFormElement} form - The form element to validate
     * @param {function(SubmitEvent): void} submitAction - Called with submit event if validation passes
     * @returns {void}
     * @throws {Error} If form is missing or submitAction is not a function
     * 
     * Required HTML attributes:
     * - data-validation-rules: Regex patterns and error messages separated by § character
     *   Format: "pattern§error_message[§pattern§error_message]..."
     * 
     * Optional HTML attributes:  
     * - aria-required="true": Makes the field required
     * - required: Native HTML required attribute
     * 
     * @example HTML
     * <form>
     *   <input type="email" 
     *     required
     *     aria-required="true"
     *     data-validation-rules="^.+@.+\\..+$§Invalid email format"
     *   />
     *   <div class="error-message"></div>
     *   <button type="submit">Submit</button>
     * </form>
     * 
     * @example Validation Rules
     * // Company name validation
     * "^\\S{1,}$§Name is required§^.{1,150}$§Max 150 chars§^\\D§Cannot start with number"
     * // Email validation
     * "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,256}$§Invalid email"
     * 
     * Note: Rules are checked in order, first failed pattern's message is shown
     * Note: All failed rules are available if we want to show them all at once.
     */
    addListeners: (form, submitAction) => {
        if (typeof submitAction !== 'function') {
            throw new Error("form-validation.js/addListeners: submitAction is not a function");
        }
        if (!form) {
            throw new Error("form-validation.js/addListeners: form is missing");
        }
        const inputs = form.querySelectorAll('input[data-validation-rules],textarea[data-validation-rules]');

        const isValid = (input, inputErrorDiv, rules) => {
            if (!input.dataset.validationActive || input.dataset.validationActive === "false") {
                // input.validity.valid = true; // getter only. Should we handle this some other way?
                return true; // fields not active are handled as valid
            }
            const { failedRules } = formValidation.testRuleSet(rules, input.value);
            const isValid = failedRules.length === 0;
            input.classList.toggle('error', !isValid);
            input.classList.toggle('valid', isValid);
            input.setAttribute('aria-invalid', !isValid);
            // input.validity.valid = isValid; // getter only. Should we handle this some other way?
            console.debug("inputErrorDiv", failedRules);
            inputErrorDiv.textContent = isValid ? '' : failedRules[0].invalidMessage;
            return isValid;
        };
        const setActive = (input, inputErrorDiv, rules) => {
            const { failedRules } = formValidation.testRuleSet(rules, input.value);
            if (failedRules.length > 0) {
                input.dataset.validationActive = "true";
                isValid(input, inputErrorDiv, rules);
            } else {
                input.dataset.validationActive = "false";
            }
        };

        inputs.forEach(inputElement => {
            const input = /** @type {HTMLInputElement} */ (inputElement);
            if (!input.dataset.validationActive) {
                input.dataset.validationActive = "false";
            }
            const inputErrorDiv = input.nextElementSibling;
            if (!inputErrorDiv) {
                throw new Error("form-validation.js/validationListener: inputErrorDiv not found " + input.id);
            }
            if (!inputErrorDiv.classList.contains('error-message')) {
                throw new Error("form-validation.js/validationListener: inputErrorDiv is missing class 'error-message' " + input.id);
            }
            const { rules, parsingErrors } = formValidation.rulesetFromString(input.dataset.validationRules);
            if (parsingErrors.length > 0) {
                throw new Error("form-validation.js/validationListener: parsing errors " + input.id + " " + parsingErrors);
            }
            // after change we start validating the input on every input or blur event
            input.addEventListener('change', () => setActive(input, inputErrorDiv, rules));
            input.addEventListener('input', () => isValid(input, inputErrorDiv, rules));
            input.addEventListener('blur', () => isValid(input, inputErrorDiv, rules));
        });

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            event.stopPropagation();
            let willPost = true;
            inputs.forEach(inputElement /** @type {HTMLInputElement} */ => {
                const input = /** @type {HTMLInputElement} */ (inputElement);
                const inputErrorDiv = input.nextElementSibling;
                if (!inputErrorDiv) {
                    console.error("form-validation.js/validationListener: inputErrorDiv not found", input);
                    return;
                }
                if (!inputErrorDiv.classList.contains('error-message')) {
                    console.error("form-validation.js/validationListener: inputErrorDiv is missing class 'error-message'", inputErrorDiv);
                    return;
                }
                const { rules, parsingErrors } = formValidation.rulesetFromString(input.dataset.validationRules);
                if (parsingErrors.length > 0 && rules.length === 0) {
                    console.error("form-validation.js/validationListener: some rules are missing, parsing errors, ", parsingErrors);
                    return;
                }

                if (parsingErrors.length > 0) {
                    console.error("form-validation.js/validationListener: failed to parse rules, ", parsingErrors, "\n\ncontinuing with remaining rules");
                }

                // on submit we validate all fields
                input.dataset.validationActive = "true";
                if (!isValid(input, inputErrorDiv, rules)) {
                    willPost = false;
                }
            });
            if (willPost) {
                submitAction(event);
            }
        });
    },

    /**
   * Validates an input element against a set of validation rules.
   * Usefull when dealing with non standard forms.
   * 
   * @param {HTMLInputElement} input - The input element to validate
   * @param {(input: HTMLInputElement, rules: ValidationRule[]) => void} callbackOk - Called with submit event if validation passes
   * @param {(input: HTMLInputElement, invalidMessage: string, failedRules: ValidationRule[]) => void} callbackError - Called with submit event if validation fails
   * @returns {function(): void} The cleanup `function`
   * @throws {Error} If callbackOk is not a `function`
   * @throws {Error} If callbackError is not a `function`
   * @throws {Error} If no rules are found
   * @throws {Error} If rule parsing errors
   * 
   * @note Only throws on setup. After setup we try to handle errors gracefully.
   */
    watchInput: (input, callbackOk, callbackError) => {
        if (typeof callbackOk !== 'function') {
            throw new Error("form-validation.js/watchInput: callbackOk is not a function");
        }
        if (typeof callbackError !== 'function') {
            throw new Error("form-validation.js/watchInput: callbackError is not a function");
        }
        const { rules, parsingErrors } = formValidation.rulesetFromString(input.dataset.validationRules);
        if (parsingErrors.length > 0) {
            throw new Error("form-validation.js/validationListener: rule parsing errors, " + parsingErrors);
        }
        if (rules.length === 0) {
            throw new Error("form-validation.js/watchInput: no rules found");
        }
        const testField = () => {
            const { failedRules, matchedRules } = formValidation.testRuleSet(rules, input.value);
            if (failedRules.length > 0) {
                callbackError(input, failedRules[0].invalidMessage, failedRules);
            } else {
                callbackOk(input, matchedRules);
            }
        }
        const setActive = () => {
            const { failedRules } = formValidation.testRuleSet(rules, input.value);
            if (failedRules.length > 0) {
                input.dataset.validationActive = "true";
                validate();
            } else {
                input.dataset.validationActive = "false";
            }
        };
        const validate = () => {
            if ((!input.dataset.validationActive || input.dataset.validationActive === "false")) {
                callbackOk(input, []);
                return;
            }

            if (!input.required && (!input.value || input.value.trim() === '')) {
                callbackOk(input, []);
                return;
            }
            testField();
        };
        input.addEventListener('change', setActive);
        input.addEventListener('input', validate);
        input.addEventListener('blur', validate);
        return () => {
            input.removeEventListener('change', setActive);
            input.removeEventListener('input', validate);
            input.removeEventListener('blur', validate);
        };
    },

    textAreaCounter: (textarea, infoMessage, max) => {
        if (!textarea) {
            throw new Error("form-validation.js/textAreaCounter: textarea is missing");
        }
        if (!infoMessage) {
            throw new Error("form-validation.js/textAreaCounter: infoMessage is missing");
        }
        if (typeof max !== 'number' || max <= 0) {
            throw new Error("form-validation.js/textAreaCounter: max must be a number and greater than 0");
        }
        infoMessage.textContent = textarea.value.length + "/" + max;
        textarea.addEventListener("input", () => {
            infoMessage.textContent = textarea.value.length + "/" + max;
        });
        textarea.setAttribute("maxlength", max);
    },

    /**
     * Injects validation rules into an input element if they for some reason can not be set in the markup. i.e. in the cms where the tags are stripped away.
     * @param {boolean} required - Controlls the aria-required and required attributes
     * @param {ValidationRuleInternational[]} ruleSet - The validation rules to inject
     * @param {HTMLInputElement} input - The input element to inject the rules into
     * @param {string} languageIsoCode - The language iso code to use for the validation messages
     * @returns {void}
     */
    injectDataTag: (required, ruleSet, input, languageIsoCode) => {
        const newRuleSet = [...ruleSet];
        if (required) {
            formValidation.required.forEach(rule => {
                newRuleSet.unshift(rule);
            });
            input.setAttribute('aria-required', 'true');
            input.required = true;
        }
        input.dataset.validationRules = formValidation.rulesetToString(newRuleSet, languageIsoCode);
    },

    /**
     * Converts an array of validation rules into a string
     * @param {ValidationRuleInternational[]} ruleset - The validation rules to convert
     * @param {string} languageIsoCode - The language iso code to use for the validation messages
     * @returns {string} The string representation of the validation rules
     * @throws {Error} If ruleSet is invalid.
     */
    rulesetToString: (ruleset, languageIsoCode) => {
        if (!Array.isArray(ruleset)) {
            throw new Error("form-validation.js/rulesetToString: ruleSet is not an array");
        }
        if (!languageIsoCode) {
            throw new Error("form-validation.js/rulesetToString: languageIsoCode is required");
        }
        var result = '';

        ruleset.forEach(rule => {
            if (!rule.pattern) {
                throw new Error("form-validation.js/rulesetToString: rule is missing pattern or invalidMessage. " + JSON.stringify(rule));
            }
            if (!rule[languageIsoCode]) {
                const message = `form-validation.js/rulesetToString: rule ('${JSON.stringify(rule)}') is missing invalidMessage for language ${languageIsoCode}`;
                throw new Error(message);
            }
            result += `${rule.pattern}§${rule[languageIsoCode]}§`;
        });
        return result;
    },

    /**
     * Parses a string of validation rules into an array of validation rules.
     * Separator: `§` (paragraph mark)
     * Format: `pattern§error_message[§pattern§error_message]...`
     * 
     * @param {string} validationString - The string containing validation rules
     * @returns {{rules: ValidationRule[], parsingErrors: ParsingError[]}} Object containing parsed rules and any parsing errors
     *
     * @example
     * const { rules, parsingErrors } = formValidation.rulesetFromString(validationString);
     * if (parsingErrors.length > 0) { 
     *     console.error("Parsing errors:", parsingErrors);
     *     return { rules: [], parsingErrors };
     * }
     * 
     * TODO: add support for more browser native input.validity values.
     */
    rulesetFromString: (validationString) => {
        let rules = [];
        let parsingErrors = [];

        if (!validationString || typeof validationString !== 'string') {
            parsingErrors.push(`Bad validation string provided: got '${validationString}', wanted a string`);
            return { rules, parsingErrors };
        }

        const segments = validationString.split('§');
        const parseSegments = (segments, rules = [], parsingErrors = []) => {
            // Base case - no more segments
            if (segments.length === 0 ||
                segments.length === 1 && segments[0].trim() === '') {
                return { rules, parsingErrors };
            }

            // Base case - one more segment
            if (segments.length === 1 && segments[0].trim() !== '') {
                parsingErrors.push('Validation string has an unpaired segment');
                parsingErrors.push(`Remaining unparsed text: "${segments[0]}"`);
            }

            const [pattern, message, ...rest] = segments;

            // Try to validate pattern is a regex
            try {
                new RegExp(pattern);
            } catch (e) {
                parsingErrors.push(`Invalid regex pattern: "${pattern}"`);
                if (pattern.startsWith('^') || pattern.endsWith('$')) {
                    parsingErrors.push('Note: Pattern looks like regex but is invalid');
                }
                return { rules, parsingErrors };
            }

            rules.push({
                pattern,
                invalidMessage: message
            });

            // Recursive case - process remaining segments
            return parseSegments(rest, rules, parsingErrors);
        };

        return parseSegments(segments);
    },
    /**
     * Tests a value against a set of validation rules
     * @param {ValidationRule[]} ruleSet
     * @param {string} value - The value to test against the rules
     * @returns {{failedRules: ValidationRule[], matchedRules: ValidationRule[]}} 
     * @example destructuring result
     * const { failed, matched } = formValidation.testRuleSet(ruleSet, '123');
     */
    testRuleSet: (ruleSet, value) => {
        let failedRules = [];
        let matchedRules = [];
        ruleSet.forEach(rule => {
            if (value.trim().match(new RegExp(rule.pattern))) {
                matchedRules.push(rule);
            } else {
                failedRules.push(rule);
            }
        });
        return { failedRules, matchedRules };
    },




    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    required: [
        {
            pattern: '\\S',
            en: 'This field is required',
            sv: 'Detta fält är obligatoriskt',
            de: 'Dieses Feld ist erforderlich',
            da: 'Dette felt er påkrævet',
            fi: 'Tämä kenttä on pakollinen',
            fr: 'Ce champ est obligatoire',
            nl: 'Dit veld is verplicht'
        }
    ]
};

